/**
 * Builds the static visual scene from the same LevelData used for physics.
 * Solids are baked to world space and merged per material: one draw for the
 * black silhouettes and one for their outlines. Solid geometry and the
 * outline look (outlineMaterials) are shared with the editor.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { eulerDegToQuat, wedgePoints, type LevelData, type SolidDef, type Vec3Tuple } from '../world/LevelData';
import type { MaterialRegistry, SurfaceMaterialDef } from '../world/SurfaceMaterials';

/** Flat-shaded wedge prism (see SolidShape): 8 triangles, outward normals. */
function wedgeGeometry(h: Vec3Tuple): THREE.BufferGeometry {
  const p = wedgePoints(h);
  const faces = [
    [0, 2, 3], [0, 3, 1], // bottom
    [0, 1, 5], [0, 5, 4], // back (−Z)
    [4, 5, 3], [4, 3, 2], // slope
    [0, 4, 2], // left (−X)
    [1, 3, 5], // right (+X)
  ];
  const pos = new Float32Array(faces.length * 9);
  const centroid = p.reduce((c, v) => [c[0] + v[0] / 6, c[1] + v[1] / 6, c[2] + v[2] / 6], [0, 0, 0]);
  faces.forEach((f, i) => {
    const [a, b, c] = f.map((k) => new THREE.Vector3(...p[k]!)) as [THREE.Vector3, THREE.Vector3, THREE.Vector3];
    const n = new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(c, a));
    const out = a.clone().add(b).add(c).divideScalar(3).sub(new THREE.Vector3(...centroid));
    // Orient every face outward regardless of how the list above is wound.
    const tri = n.dot(out) < 0 ? [a, c, b] : [a, b, c];
    tri.forEach((v, j) => pos.set([v.x, v.y, v.z], i * 9 + j * 3));
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  // Indexed like boxes and cylinders, so solids sharing a material can be merged.
  g.setIndex([...Array(faces.length * 3).keys()]);
  g.computeVertexNormals();
  return g;
}

/** World-space render geometry of one solid. Shared by gameplay rendering and the editor (Phase 4). */
export function solidGeometry(s: SolidDef): THREE.BufferGeometry {
  const g =
    s.shape.type === 'box'
      ? new THREE.BoxGeometry(s.shape.halfExtents[0] * 2, s.shape.halfExtents[1] * 2, s.shape.halfExtents[2] * 2)
      : s.shape.type === 'cylinder'
        ? new THREE.CylinderGeometry(s.shape.radius, s.shape.radius, s.shape.halfHeight * 2, 40, 1)
        : wedgeGeometry(s.shape.halfExtents);
  const q = eulerDegToQuat(s.rotationDeg, { x: 0, y: 0, z: 0, w: 1 });
  const m = new THREE.Matrix4().compose(new THREE.Vector3(...s.position), new THREE.Quaternion(q.x, q.y, q.z, q.w), new THREE.Vector3(1, 1, 1));
  g.applyMatrix4(m);
  // Untextured: drop the primitives' UVs so every shape has the same attributes and merges with the rest.
  g.deleteAttribute('uv');
  return g;
}

/** A lantern's sides: an iron frame around four glowing panes (white, so each lantern's colour tints them). */
function lanternTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#1a1410';
    ctx.fillRect(0, 0, 64, 64);
    for (const x of [8, 34]) {
      for (const y of [8, 34]) {
        const g = ctx.createRadialGradient(x + 11, y + 11, 2, x + 11, y + 11, 16);
        g.addColorStop(0, '#ffffff');
        g.addColorStop(1, '#d9d0c0');
        ctx.fillStyle = g;
        ctx.fillRect(x, y, 22, 22);
      }
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** A floating text sprite; the caller disposes its material and texture. */
export function labelSprite(text: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 96;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.font = '600 44px "Cinzel", Georgia, serif';
    ctx.fillStyle = '#c9b6ff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = '#7b5cff';
    ctx.shadowBlur = 16;
    ctx.fillText(text, 256, 48);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
  sprite.scale.set(8, 1.5, 1);
  return sprite;
}

/**
 * The game's look: every solid is a black silhouette traced with bright
 * outlines on its edges, in a black, foggy void. Nothing is lit or textured;
 * the black fill hides the outlines behind it, and the fog fades distant ones.
 * Hazards keep a dim fill and a coloured outline so a lethal edge never looks
 * safe (and differs in brightness as well as colour).
 */
const EDGE_ANGLE_DEG = 35;
const EDGE = { safe: { color: 0xffffff, opacity: 0.8 }, lethal: { color: 0xff3848, opacity: 1 }, reactive: { color: 0x6ff2ff, opacity: 1 } };
/** Fill brightness (fraction of the material's glow colour) for hazards and reactive surfaces; safe solids are black. */
const HAZARD_FILL = 0.12;

export interface SceneViewOptions {
  /** Accessibility: full-strength outlines. */
  highContrast: boolean;
  /** Colour of safe outlines (0xRRGGBB); hazards keep theirs. */
  outlineColor: number;
}

const DEFAULT_OPTIONS: SceneViewOptions = { highContrast: false, outlineColor: EDGE.safe.color };

export interface OutlineMaterials {
  /** The silhouette fill. */
  fill: THREE.MeshBasicMaterial;
  edge: THREE.LineBasicMaterial;
  /** The edge's normal opacity (high contrast raises it to 1). */
  opacity: number;
}

/** A surface material's look: its fill and outline (shared with the editor). Safe edges take `safeColor`. */
export function outlineMaterials(def: SurfaceMaterialDef, safeColor: number = EDGE.safe.color): OutlineMaterials {
  const glow = def.kind === 'lethal' || def.kind === 'reactive' ? new THREE.Color(def.visual.emissive || def.visual.color).multiplyScalar(HAZARD_FILL) : new THREE.Color(0);
  const style = def.kind === 'lethal' ? EDGE.lethal : def.kind === 'reactive' ? EDGE.reactive : EDGE.safe;
  return {
    // Pushed back slightly, so the outlines along its own edges are never hidden by it.
    fill: new THREE.MeshBasicMaterial({ color: glow, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 }),
    edge: new THREE.LineBasicMaterial({ color: style === EDGE.safe ? safeColor : style.color, transparent: true, opacity: style.opacity, depthWrite: false }),
    opacity: style.opacity,
  };
}

/** The outline of a solid's render geometry; the caller disposes it. */
export function edgeGeometry(geometry: THREE.BufferGeometry): THREE.EdgesGeometry {
  return new THREE.EdgesGeometry(geometry, EDGE_ANGLE_DEG);
}

export class SceneView {
  readonly group = new THREE.Group();
  private readonly disposables: { dispose(): void }[] = [];
  private readonly outlines: { material: THREE.LineBasicMaterial; opacity: number; safe: boolean }[] = [];
  drawCallEstimate = 0;
  triangleCount = 0;

  constructor(level: LevelData, materials: MaterialRegistry, options: Partial<SceneViewOptions> = {}) {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    this.group.name = 'level';
    const byMaterial = new Map<string, THREE.BufferGeometry[]>();
    for (const s of level.solids) {
      const list = byMaterial.get(s.material) ?? [];
      list.push(solidGeometry(s));
      byMaterial.set(s.material, list);
    }
    for (const [matId, geoms] of byMaterial) {
      const merged = mergeGeometries(geoms, false);
      for (const g of geoms) g.dispose();
      if (!merged) throw new Error(`SceneView: could not merge geometry for material ${matId}`);
      const def = materials.get(matId);
      const { fill: mat, edge: edgeMat, opacity } = outlineMaterials(def, opts.outlineColor);
      const mesh = new THREE.Mesh(merged, mat);
      mesh.name = `solids:${matId}`;
      this.group.add(mesh);
      const edgeGeo = edgeGeometry(merged);
      this.outlines.push({ material: edgeMat, opacity, safe: def.kind !== 'lethal' && def.kind !== 'reactive' });
      const lines = new THREE.LineSegments(edgeGeo, edgeMat);
      lines.name = `edges:${matId}`;
      this.group.add(lines);
      this.disposables.push(merged, mat, edgeGeo, edgeMat);
      this.drawCallEstimate += 2;
      this.triangleCount += (merged.index?.count ?? merged.getAttribute('position').count) / 3;
    }

    // Trigger zones (start, checkpoints, finish) and track labels are not drawn in play: the HUD and sounds
    // mark the start, splits and finish. The editor still shows them (ObjectViews).
    // Lanterns are small glowing boxes (one instanced draw); they light nothing.
    const lanterns = level.lights ?? [];
    if (lanterns.length > 0) {
      const lanternGeo = new THREE.BoxGeometry(0.34, 0.46, 0.34);
      const lanternTex = lanternTexture();
      const lanternMat = new THREE.MeshBasicMaterial({ color: 0xffffff, map: lanternTex });
      const bulbs = new THREE.InstancedMesh(lanternGeo, lanternMat, lanterns.length);
      bulbs.name = 'lantern-bulbs';
      const m = new THREE.Matrix4();
      const c = new THREE.Color();
      lanterns.forEach((l, i) => {
        bulbs.setMatrixAt(i, m.makeTranslation(...l.position));
        // The panes take their lantern's colour, lifted toward white so they read as a light source.
        bulbs.setColorAt(i, c.set(l.color).lerp(new THREE.Color(0xffffff), 0.35));
      });
      this.group.add(bulbs);
      this.disposables.push(lanternTex, lanternGeo, lanternMat, bulbs);
      this.drawCallEstimate++;
    }
    this.setHighContrast(opts.highContrast);
  }

  /** Recolours the safe outlines (hazard outlines never change). */
  setOutlineColor(color: number): void {
    for (const o of this.outlines) if (o.safe) o.material.color.setHex(color);
  }

  /** High contrast draws every outline at full strength. */
  setHighContrast(on: boolean): void {
    for (const o of this.outlines) o.material.opacity = on ? 1 : o.opacity;
  }

  dispose(): void {
    for (const d of this.disposables) d.dispose();
    this.group.clear();
  }
}
